import { ICoreContext } from '@msdyn365-commerce/core';
import { AsyncResultStatus, Customer } from '@msdyn365-commerce/retail-proxy';
import { useEffect } from 'react';

import { ensureCustomerPrimaryLoyaltyCard, getCustomerPrimaryLoyaltyCardId } from '../../../shared/functions/loyalty';
import { isUserAuthenticated } from '../../../utils/is-user-authenticated';

export interface IUseLoyaltyCardSyncOptions {
    context: ICoreContext;
    customer?: Customer;
    customerStatus: AsyncResultStatus;
}

/**
 * Ensures the authenticated customer has a primary loyalty card, mirroring the
 * legacy header behavior. Customers that already carry the primary-card
 * extension property are skipped. Card lookup and issuance are delegated to
 * the shared loyalty service, which dedupes concurrent issuance across modules.
 */
export const useLoyaltyCardSync = (options: IUseLoyaltyCardSyncOptions): void => {
    const { context, customer, customerStatus } = options;

    const isAuthenticated = isUserAuthenticated(context.request);
    const accountNumber = customer?.AccountNumber;

    useEffect(() => {
        if (typeof window === 'undefined' || !isAuthenticated || customerStatus !== 'SUCCESS' || !customer || !accountNumber) {
            return;
        }

        if (getCustomerPrimaryLoyaltyCardId(customer)) {
            return;
        }

        ensureCustomerPrimaryLoyaltyCard(context, customer).catch((error: Error) => {
            context.telemetry.error(`[Loyalty] Failed to ensure primary loyalty card - Account: ${accountNumber} - ${error.message}`);
        });
    }, [context, customer, customerStatus, isAuthenticated, accountNumber]);
};
